import { CreatorBrand, QuicksPicksBrand } from 'constants/brands';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';
import React from 'react';
import {
    Nav,
    MusicNote,
    SearchOutlined,
    HeadphonesOutlined,
    ProductPluginsOutlined,
    useBreakpoint,
    ArchiveOutlined,
    MaestroComponent,
    IconProps,
    ProductHeader,
    ProductSamplesOutlined,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { RoutePaths } from 'types/enums';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { getIsCurrentPath } from 'helpers';
import { CONFIG } from 'utils/config';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { useFlag } from 'hooks/useFlag/useFlag';
import { FeatureFlag } from 'types/enums';
import { NavigationDropdown, DropdownItemsProps } from './NavigationDropdown';

const usePublicNavigation = () => {
    const { isAuthenticated } = useAuthentication();
    const toLocalizedPath = useToLocalizedPath();

    const forYou = {
        path: toLocalizedPath('/'),
        text: t`For you`,
        icon: MusicNote,
    };

    const search = {
        path: toLocalizedPath(`/${RoutePaths.Browse}`),
        text: t`Search`,
        icon: SearchOutlined,
    };

    const quickPicks = {
        path: toLocalizedPath(`/${RoutePaths.QuickPicks}`),
        text: QuicksPicksBrand,
        icon: ArchiveOutlined,
    };

    const creator = {
        path: toLocalizedPath(`/${RoutePaths.CreatorBeatMaker}`),
        text: CreatorBrand,
        icon: HeadphonesOutlined,
        isBrowserOnly: true,
    };

    const pluginSamples = {
        path: isAuthenticated
            ? `${CONFIG.VITE_LANDR_APP_DOMAIN_URL}/plugins/details/landr-samples`
            : `${CONFIG.VITE_GUESTSITE_URL}/plugins/landr-samples-plugin`,
        title: t`Samples plugin`,
        icon: ProductSamplesOutlined,
        isBrowserOnly: true,
        subTitle: t`Browse for the perfect samples for your track and pull them right in your DAW.`,
        target: '_blank',
    };

    const pluginChromatic = {
        path: isAuthenticated
            ? `${CONFIG.VITE_LANDR_APP_DOMAIN_URL}/plugins/details/chromatic`
            : `${CONFIG.VITE_GUESTSITE_URL}/plugins/chromatic`,
        title: `Chromatic`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        subTitle: t`Explore new creative territories with a loop-based instrument plugin.`,
        target: '_blank',
    };

    const plugins = {
        path: CONFIG.VITE_SAMPLES_PLUGIN_URL,
        text: t`Plugins`,
        icon: ProductPluginsOutlined,
        isBrowserOnly: true,
        target: '_blank',
        dropdownItems: [pluginSamples, pluginChromatic],
    };

    type Route = {
        path: string;
        text: string;
        icon: MaestroComponent<IconProps, Record<string, any>>;
        isBrowserOnly?: boolean;
        target?: React.HTMLAttributeAnchorTarget;
        dropdownItems?: Array<DropdownItemsProps>;
    };

    const forYouRoutes: Route[] = [
        forYou,
        search,
        quickPicks,
        creator,
        plugins,
    ];

    const isMobile = useBreakpoint('md');

    const publicRoutes = forYouRoutes;

    return { isMobile, publicRoutes };
};

export const PublicNavigation = ({
    onClick,
    isDAWPlugin,
}: {
    onClick: () => void;
    isDAWPlugin: boolean;
}) => {
    const { publicRoutes, isMobile } = usePublicNavigation();
    const isChromaticMS2Enabled = useFlag(FeatureFlag.ChromaticMS2, true);

    return (
        <ProductHeader.Nav
            key={`public-chromatic-ff-${isChromaticMS2Enabled ? 'on' : 'off'}`}
        >
            {publicRoutes
                .filter(({ isBrowserOnly }) =>
                    isDAWPlugin ? !isBrowserOnly : true,
                )
                .map((props) => (
                    <>
                        {isChromaticMS2Enabled &&
                        props.dropdownItems &&
                        props.dropdownItems.length > 0 ? (
                            <NavigationDropdown
                                key={props.text}
                                text={props.text}
                                dropdownItems={props.dropdownItems}
                                onClick={onClick}
                                isMobile={isMobile}
                                icon={props.icon}
                            ></NavigationDropdown>
                        ) : (
                            <Nav.Link
                                key={props.text}
                                as={RouterLink}
                                to={props.path}
                                icon={isMobile && props.icon}
                                onClick={onClick}
                                isCurrent={getIsCurrentPath(props.path)}
                                target={props?.target}
                            >
                                {props.text}
                            </Nav.Link>
                        )}
                    </>
                ))}
        </ProductHeader.Nav>
    );
};
